import { useState, useRef, useEffect, useLayoutEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Plus, X, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

interface DynamicListInputProps {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  addLabel?: string;
  emptyMessage?: string;
  maxItems?: number;
  showNumbers?: boolean;
  /** Permite arrastar para reordenar (ícone entre o número e o campo). */
  sortable?: boolean;
}

function newId() {
  return crypto.randomUUID();
}

function SortableListRow({
  id,
  index,
  item,
  showNumbers,
  placeholder,
  inputRefs,
  updateItem,
  removeItem,
  handleKeyDown,
  autoResize,
}: {
  id: string;
  index: number;
  item: string;
  showNumbers: boolean;
  placeholder: string;
  inputRefs: React.MutableRefObject<(HTMLTextAreaElement | null)[]>;
  updateItem: (index: number, value: string) => void;
  removeItem: (index: number) => void;
  handleKeyDown: (e: React.KeyboardEvent, index: number) => void;
  autoResize: (el: HTMLTextAreaElement | null) => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-start gap-2 group agent-field-enter",
        isDragging && "opacity-60 z-10 relative"
      )}
    >
      {showNumbers && (
        <span className="text-sm text-muted-foreground w-6 flex-shrink-0 pt-2">{index + 1}.</span>
      )}
      <button
        type="button"
        ref={setActivatorNodeRef}
        className={cn(
          "flex-shrink-0 mt-2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/80 cursor-grab active:cursor-grabbing touch-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
        )}
        aria-label="Arrastar para reordenar"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <textarea
        ref={(el) => {
          inputRefs.current[index] = el;
          autoResize(el);
        }}
        value={item}
        onChange={(e) => {
          updateItem(index, e.target.value.replace(/\n/g, " "));
          autoResize(e.target);
        }}
        onKeyDown={(e) => handleKeyDown(e, index)}
        placeholder={placeholder}
        rows={1}
        className="flex-1 transition-all focus:ring-2 focus:ring-agent-primary/20 resize-none overflow-hidden break-words flex min-h-[36px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => removeItem(index)}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-md mt-1"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

export function DynamicListInput({
  items,
  onChange,
  placeholder = "Digite aqui...",
  addLabel = "Adicionar",
  emptyMessage = "Nenhum item adicionado",
  maxItems = 30,
  showNumbers = true,
  sortable = false,
}: DynamicListInputProps) {
  const [newItemIndex, setNewItemIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const [sortableIds, setSortableIds] = useState<string[]>(() => (sortable ? items.map(() => newId()) : []));

  useLayoutEffect(() => {
    if (!sortable) {
      setSortableIds([]);
      return;
    }
    setSortableIds((prev) => {
      if (prev.length === items.length) return prev;
      if (prev.length > items.length) return prev.slice(0, items.length);
      const next = [...prev];
      while (next.length < items.length) next.push(newId());
      return next;
    });
  }, [items.length, sortable]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
  };

  useEffect(() => {
    if (newItemIndex !== null && inputRefs.current[newItemIndex]) {
      inputRefs.current[newItemIndex]?.focus();
      setNewItemIndex(null);
    }
  }, [newItemIndex, items.length]);

  const addItem = () => {
    if (items.length < maxItems) {
      onChange([...items, ""]);
      setNewItemIndex(items.length);
    }
  };

  const updateItem = (index: number, value: string) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (items[index]?.trim() !== "") {
        addItem();
      }
    } else if (e.key === "Backspace" && items[index] === "" && items.length > 0) {
      e.preventDefault();
      removeItem(index);
      if (index > 0 && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    setSortableIds((ids) => arrayMove(ids, oldIndex, newIndex));
    onChange(arrayMove(items, oldIndex, newIndex));
  };

  const staticRow = (item: string, index: number) => (
    <div key={index} className={cn("flex items-start gap-2 group agent-field-enter")}>
      {showNumbers && (
        <span className="text-sm text-muted-foreground w-6 flex-shrink-0">{index + 1}.</span>
      )}
      <textarea
        ref={(el) => {
          inputRefs.current[index] = el;
          autoResize(el);
        }}
        value={item}
        onChange={(e) => {
          updateItem(index, e.target.value.replace(/\n/g, " "));
          autoResize(e.target);
        }}
        onKeyDown={(e) => handleKeyDown(e, index)}
        placeholder={placeholder}
        rows={1}
        className="flex-1 transition-all focus:ring-2 focus:ring-agent-primary/20 resize-none overflow-hidden break-words flex min-h-[36px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => removeItem(index)}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-md"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">{emptyMessage}</p>
      ) : sortable && sortableIds.length === items.length ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {items.map((item, index) => (
              <SortableListRow
                key={sortableIds[index]}
                id={sortableIds[index]}
                index={index}
                item={item}
                showNumbers={showNumbers}
                placeholder={placeholder}
                inputRefs={inputRefs}
                updateItem={updateItem}
                removeItem={removeItem}
                handleKeyDown={handleKeyDown}
                autoResize={autoResize}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        items.map((item, index) => staticRow(item, index))
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addItem}
        disabled={items.length >= maxItems}
        className="w-full border-dashed"
      >
        <Plus className="w-4 h-4 mr-2" /> {addLabel}
      </Button>

      {items.length >= maxItems && (
        <p className="text-xs text-muted-foreground text-center">Limite de {maxItems} itens atingido</p>
      )}
    </div>
  );
}
